/**
 * PainterOps Plugin Registry
 *
 * Every feature in PainterOps is a plugin. Core plugins are always on.
 * Add-on plugins can be toggled per account from the Settings screen.
 *
 * To add a new plugin:
 * 1. Add it here with a unique id
 * 2. Create the component/screen in src/plugins/<id>/
 * 3. Register any routes or sidebar items it needs
 * 4. Use useFeature('your_id') to conditionally render it anywhere
 */

export const PLUGINS = [
  // ── CORE (always enabled, cannot disable) ──────────────────
  {
    id: 'customers',
    name: 'Customer Management',
    category: 'core',
    icon: '👤',
    core: true,
    description: 'Manage pool customers, contacts, gate codes, and full service history.',
    route: '/customers',
  },
  {
    id: 'routes',
    name: 'Route Scheduling',
    category: 'core',
    icon: '🗺️',
    core: true,
    description: 'Schedule and manage daily service routes, stop order, and technician assignments.',
    route: '/routes',
  },
  {
    id: 'chem_log',
    name: 'Chemical Logging',
    category: 'core',
    icon: '🧪',
    core: true,
    description: 'Log water chemistry readings and dosing at every stop, with LSI calculation.',
  },
  {
    id: 'photos',
    name: 'Photo Proof',
    category: 'core',
    icon: '📸',
    core: true,
    description: 'Capture before/after service photos with configurable required gates per stop.',
  },
  {
    id: 'billing',
    name: 'Invoicing & Billing',
    category: 'core',
    icon: '💳',
    core: true,
    description: 'Create invoices, track payments, manage billing history.',
    route: '/billing',
  },
  {
    id: 'team',
    name: 'Team Management',
    category: 'core',
    icon: '👥',
    core: true,
    description: 'Manage technicians, assign routes, track performance metrics.',
    route: '/team',
  },

  // ── AI & AUTOMATION ────────────────────────────────────────
  {
    id: 'ai_routing',
    name: 'AI Route Optimization',
    category: 'ai',
    icon: '⚡',
    core: false,
    description: 'Automatically optimize daily routes using AI to minimize drive time and fuel costs.',
  },
  {
    id: 'ai_chem',
    comingSoon: true,
    name: 'AI Chem Recommendations',
    category: 'ai',
    icon: '🤖',
    core: false,
    description: 'AI-powered dosing recommendations based on current readings and each pool\'s history.',
  },
  {
    id: 'ai_receptionist',
    comingSoon: true,
    name: 'AI Receptionist',
    category: 'ai',
    icon: '📞',
    core: false,
    description: '24/7 call answering, lead capture, and instant summary emails to the owner.',
  },
  {
    id: 'predictive',
    comingSoon: true,
    name: 'Predictive Equipment Alerts',
    category: 'ai',
    icon: '🔮',
    core: false,
    description: 'AI predicts equipment failures 2–4 weeks before they happen using usage patterns.',
  },

  // ── FIELD OPERATIONS ──────────────────────────────────────
  {
    id: 'geofencing',
    name: 'Geofencing & Arrival Alerts',
    category: 'field',
    icon: '📍',
    core: false,
    description: 'Auto-detect when techs arrive at and leave customer locations. Prompt to mark arrived. Alert on prolonged stays.',
  },
  {
    id: 'voice_logging',
    comingSoon: true,
    name: 'Voice Chem Entry',
    category: 'field',
    icon: '🎤',
    core: false,
    description: 'Speak chemical readings aloud instead of manually typing — faster poolside logging.',
  },
  {
    id: 'bluetooth_sync',
    comingSoon: true,
    name: 'Bluetooth Tester Sync',
    category: 'field',
    icon: '📶',
    core: false,
    description: 'Sync readings directly from LaMotte or Taylor water testers via Bluetooth.',
  },

  // ── EQUIPMENT MONITORING ──────────────────────────────────
  {
    id: 'eq_pentair',
    comingSoon: true,
    name: 'Pentair IntelliCenter',
    category: 'monitoring',
    icon: '⚙️',
    core: false,
    description: 'Live equipment monitoring via Pentair IntelliCenter local + cloud API.',
    docsUrl: 'https://docs.painterops.com/plugins/pentair',
  },
  {
    id: 'eq_jandy',
    comingSoon: true,
    name: 'Jandy iAquaLink',
    category: 'monitoring',
    icon: '🌊',
    core: false,
    description: 'Live equipment monitoring via Jandy iAquaLink 2.0 cloud API.',
    docsUrl: 'https://docs.painterops.com/plugins/jandy',
  },
  {
    id: 'eq_hayward',
    comingSoon: true,
    name: 'Hayward OmniLogic',
    category: 'monitoring',
    icon: '🌿',
    core: false,
    description: 'Live equipment monitoring via Hayward OmniLogic/OmniHub API.',
    docsUrl: 'https://docs.painterops.com/plugins/hayward',
  },
  {
    id: 'eq_attendant',
    comingSoon: true,
    name: 'Pool Attendant Sensors',
    category: 'monitoring',
    icon: '📡',
    core: false,
    description: 'Remote water chemistry sensor monitoring via Pool Attendant partner API.',
    docsUrl: 'https://docs.painterops.com/plugins/pool-attendant',
  },

  // ── BUSINESS GROWTH ────────────────────────────────────────
  {
    id: 'autopay',
    name: 'Autopay Enrollment',
    category: 'business',
    icon: '💰',
    core: false,
    description: 'Automatic monthly billing via Stripe — customers opt-out, not opt-in.',
  },
  {
    id: 'customer_portal',
    name: 'Customer Portal',
    category: 'business',
    icon: '🏠',
    core: false,
    description: 'White-labeled portal where customers view pool health, photos, and invoices.',
  },
  {
    id: 'review_requests',
    comingSoon: true,
    name: 'Auto Review Requests',
    category: 'business',
    icon: '⭐',
    core: false,
    description: 'Automatically send Google review requests after positive service visits.',
  },
  {
    id: 'pricing_intel',
    comingSoon: true,
    name: 'Pricing Intelligence',
    category: 'business',
    icon: '📈',
    core: false,
    description: 'Flag underpriced accounts based on local market data comparisons.',
  },
  {
    id: 'marketing',
    comingSoon: true,
    name: 'Marketing & CRM',
    category: 'business',
    icon: '📣',
    core: false,
    description: 'Lead pipeline, seasonal reactivation campaigns, and referral tracking.',
  },

  // ── ANALYTICS ─────────────────────────────────────────────
  {
    id: 'analytics_pro',
    comingSoon: true,
    name: 'Advanced Analytics',
    category: 'analytics',
    icon: '📊',
    core: false,
    description: 'Profit-per-route, chem cost tracking, technician performance dashboards.',
  },
];

export const PLUGIN_CATEGORIES = {
  core:       { label: 'Core Features',          description: 'Always active — the foundation of PainterOps' },
  ai:         { label: 'AI & Automation',         description: 'Intelligent features powered by machine learning' },
  field:      { label: 'Field Operations',         description: 'Tools that make life easier for technicians' },
  monitoring: { label: 'Equipment Monitoring',     description: 'Live integration with pool automation controllers' },
  business:   { label: 'Business Growth',          description: 'Features to grow revenue and retain customers' },
  analytics:  { label: 'Advanced Analytics',       description: 'Deep business intelligence and reporting' },
};

// Helper to get plugins by category
export const pluginsByCategory = () => {
  return Object.keys(PLUGIN_CATEGORIES).reduce((acc, cat) => {
    acc[cat] = PLUGINS.filter(p => p.category === cat);
    return acc;
  }, {});
};
